/**
 * 2026.1 — THRESHOLDS (FY2025-26 — 1 Jul 2025 → 30 Jun 2026).
 *
 * Bright-line dollar amounts that flip behaviour on/off. Each value
 * cites its ATO source. This file follows the same lifecycle as
 * `rates.ts`: FROZEN once confirmed; changes = new ruleset version.
 */

import type { AtoThresholds } from "../types";

export const thresholds: AtoThresholds = {
  status: "confirmed",

  /**
   * GST-registration turnover threshold. Businesses MUST register for
   * GST once GST turnover (current or projected) reaches this level.
   * Ride-share drivers register regardless of turnover; not-for-profits
   * use a $150,000 threshold — surface either as separate rulesets in
   * the future if the app grows to serve them.
   */
  gstRegistration: {
    amount: 75000,
    unit: "AUD/year",
    source:
      "ATO — Registering for GST: https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/registering-for-gst",
  },

  /**
   * Instant asset write-off — full deduction in the year of first-use
   * for eligible assets costing LESS than this amount. Small business
   * entities (aggregated turnover < $10M) using simplified depreciation.
   *
   * FY25-26 threshold: $20,000 per asset. Asset must be first used or
   * installed ready for use by 30 Jun 2026. From 1 Jul 2026 the
   * statutory baseline reverts to $1,000 unless Parliament extends —
   * that will be captured in a future ruleset once legislated.
   *
   * GST-registered businesses measure against the $20,000 exclusive of
   * GST; non-GST-registered businesses measure inclusive of GST.
   */
  instantAssetWriteOff: {
    amount: 20000,
    unit: "AUD",
    source:
      "ATO — Instant asset write-off: https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/depreciation-and-capital-expenses-and-allowances/simpler-depreciation-for-small-business/instant-asset-write-off",
  },

  /**
   * Small-business turnover ceiling for the "small business entity"
   * (SBE) concessions — simplified depreciation, PAYG concessions, GST
   * cash accounting, etc. Aggregated turnover measured across the
   * entity + connected entities + affiliates.
   */
  smallBusinessBasCeiling: {
    amount: 10000000,
    unit: "AUD/year",
    source:
      "ATO — Small business entity concessions: https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/small-business-entity-concessions/eligibility",
  },

  /**
   * PAYG withholding registration — no dollar turnover threshold.
   * A business must register once it pays employees, some contractors
   * under a voluntary agreement, or foreign residents. Left null so the
   * knowledge base surface communicates "no numeric threshold applies".
   */
  paygWithholdingRegistration: null,
};
